import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { createWalletOrder, saveWalletPayment } from "../../features/wallet/WalletServices";
import { getAuthUser, getDataUserId } from "../../util/manageAuth";

import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";
import { IoArrowBack, IoWalletOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { loadRazorpayScript } from "../../util/razorpay";

const QUICK_AMOUNTS = [100, 250, 500, 1000, 2000, 5000];

export default function AddWallet() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const authUser = getAuthUser();
    const userId = getDataUserId();

    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleQuickSelect = (val: number) => setAmount(String(val));

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleaned = e.target.value.replace(/[^0-9]/g, "");
        setAmount(cleaned);
    };

    // ── Main handler ─────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        const parsed = Number(amount);
        if (!amount || parsed <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }
        if (!userId) {
            toast.error("User not found");
            return;
        }

        setSubmitting(true);

        // Load Razorpay script first
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
            toast.error("Failed to load payment gateway. Check your connection.");
            setSubmitting(false);
            return;
        }

        try {
            // STEP 1 — Create order on backend (only sends amount)
            const order = await dispatch(
                createWalletOrder({ amount: parsed })
            ).unwrap();

            if (order.status !== "created") {
                toast.error("Failed to create payment order. Please try again.");
                setSubmitting(false);
                return;
            }

            // STEP 2 — Open Razorpay modal
            openRazorpay({ order, displayAmount: parsed });

        } catch (err: any) {
            toast.error(err?.message || "Failed to initiate payment");
            setSubmitting(false);
        }
    };

    // ── Razorpay checkout ─────────────────────────────────────────────────────

    const openRazorpay = ({
        order,
        displayAmount,
    }: {
        order: { id: string; amount: number; currency: string };
        displayAmount: number;
    }) => {
        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY || "",
            amount: order.amount,       // in paise from backend
            currency: order.currency,
            name: "Wallet Top-up",
            description: `Adding ₹${displayAmount.toLocaleString("en-IN")} to wallet`,
            order_id: order.id,

            // STEP 3 — Payment success → save to DB
            handler: async (response: {
                razorpay_order_id: string;
                razorpay_payment_id: string;
                razorpay_signature: string;
            }) => {
                try {
                    await dispatch(
                        saveWalletPayment({
                            userId: Number(getDataUserId()),
                            mobileNumber: authUser?.mobile || "",
                            emailId: authUser?.email || "",
                            amount: displayAmount,
                            razorpayPaymentId: response.razorpay_payment_id,
                            razorpayOrderId: response.razorpay_order_id,
                            paymentStatus: "SUCCESS",
                        })
                    ).unwrap();

                    toast.success(`₹${displayAmount.toLocaleString("en-IN")} added to wallet!`);
                    navigate(-1);
                } catch (err: any) {
                    toast.error(err?.message || "Payment saved but wallet update failed. Contact support.");
                } finally {
                    setSubmitting(false);
                }
            },

            modal: {
                ondismiss: () => {
                    toast.info("Payment cancelled");
                    setSubmitting(false);
                },
            },

            prefill: {
                name: authUser?.userName || "",
                email: authUser?.email || "",
                contact: authUser?.mobile || "",
            },

            theme: {
                color: "#2563EB",
            },
        };

        const rzp = new window.Razorpay(options);

        // Payment failure → still save FAILED status to DB
        rzp.on("payment.failed", async (response: any) => {
            try {
                await dispatch(
                    saveWalletPayment({
                        userId: Number(getDataUserId()),
                        mobileNumber: authUser?.mobile || "",
                        emailId: authUser?.email || "",
                        amount: displayAmount,
                        razorpayPaymentId: response.error?.metadata?.payment_id || "",
                        razorpayOrderId: response.error?.metadata?.order_id || "",
                        paymentStatus: "FAILED",
                    })
                ).unwrap();
            } catch (_) {
                // silently ignore DB save failure on failed payment
            }

            toast.error(response?.error?.description || "Payment failed. Please try again.");
            setSubmitting(false);
        });

        rzp.open();
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <div className="flex gap-3 items-center w-max">
                        <IoArrowBack
                            className="text-2xl cursor-pointer hover:text-brand-main transition"
                            onClick={() => navigate(-1)}
                        />
                        <span className="font-bold text-lg uppercase">Add to Wallet</span>
                    </div>
                </CustomNavbar>
            }
        >
            <div className="bg-white rounded-4xl p-7 flex flex-col gap-8 max-w-lg mx-auto">

                <div className="flex flex-col items-center gap-2 pt-2">
                    <div className="bg-brand-main/10 p-4 rounded-full">
                        <IoWalletOutline className="text-4xl text-brand-main" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">Enter Amount</h2>
                    <p className="text-sm text-gray-400 text-center">
                        Choose a quick amount or type a custom value below.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    {QUICK_AMOUNTS.map((val) => (
                        <button
                            key={val}
                            onClick={() => handleQuickSelect(val)}
                            className={`py-2 rounded-xl border text-sm font-semibold transition
                                ${amount === String(val)
                                    ? "bg-brand-main text-white border-brand-main shadow"
                                    : "border-gray-200 text-gray-600 hover:border-brand-main hover:text-brand-main"
                                }`}
                        >
                            ₹{val.toLocaleString("en-IN")}
                        </button>
                    ))}
                </div>

                <div className="flex flex-col gap-2">
                    <label className="font-bold text-sm text-gray-700">Custom Amount (₹)</label>
                    <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-lg">
                            ₹
                        </span>
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={amount}
                            onChange={handleAmountChange}
                            className="w-full border rounded-xl pl-8 pr-4 py-3 text-xl font-bold outline-none focus:ring-2 focus:ring-brand-main text-gray-800 tracking-wide"
                        />
                    </div>
                    {amount && Number(amount) > 0 && (
                        <p className="text-xs text-gray-400 pl-1">
                            Adding{" "}
                            <span className="font-semibold text-brand-main">
                                ₹{Number(amount).toLocaleString("en-IN")}
                            </span>{" "}
                            to wallet
                        </p>
                    )}
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={submitting || !amount || Number(amount) <= 0}
                    className="w-full py-3 bg-brand-blue-gradient text-white font-semibold text-lg rounded-xl shadow hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting ? "Opening Payment..." : "Add to Wallet"}
                </button>
            </div>
        </PageLayout>
    );
}
